import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { catchError, switchMap, timeout, EMPTY, Observable, TimeoutError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';

let redirectInProgress = false;

/**
 * HTTP Interceptor that attaches JWT token from MSAL to outgoing requests
 * and handles 401 responses by attempting a silent token refresh before
 * falling back to a login redirect.
 */
export const msalTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const msalService = inject(MsalService);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip token attachment for local JSON files
  if (req.url.includes('.json') || req.url.startsWith('data/')) {
    return next(req);
  }

  // Skip token attachment for external URLs that don't need authentication
  if (!shouldAttachToken(req.url)) {
    return next(req);
  }

  const activeAccount = msalService.instance.getActiveAccount();

  if (!activeAccount) {
    return next(req);
  }

  return makeAuthenticatedRequest(msalService, authService, router, req, next, activeAccount, false);
};

function makeAuthenticatedRequest(
  msalService: MsalService,
  authService: AuthService,
  router: Router,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  activeAccount: AccountInfo,
  forceRefresh: boolean
): Observable<HttpEvent<unknown>> {
  return msalService.acquireTokenSilent({
    scopes: environment.msal.apiScopes,
    account: activeAccount,
    forceRefresh
  }).pipe(
    // MSAL's silent token acquisition can hang indefinitely once the cached refresh token has
    // expired (guaranteed after any long idle period, e.g. overnight) — it falls back to a
    // hidden-iframe SSO check gated behind a single shared in-flight slot on the MSAL client
    // instance, so every concurrent acquireTokenSilent call app-wide queues behind that one
    // attempt. This bounds our wait so a stuck gate always yields a next/error signal instead of
    // leaving every route silently frozen.
    timeout(20000),
    catchError(err => {
      // Token acquisition itself failed (expired refresh token, consent required, timed out,
      // etc.) — this catchError must stay scoped to acquireTokenSilent only. If it also wrapped
      // the downstream HTTP call (e.g. via a single .pipe() covering both), any non-401 API error
      // re-thrown below would land here too, get misread as a token failure, and silently retry
      // unauthenticated — discarding the real error (this previously masked real 4xx/5xx detail
      // messages app-wide).
      if (err instanceof InteractionRequiredAuthError || err instanceof TimeoutError || (err as Error).name === 'BrowserAuthError') {
        redirectToLogin(msalService, authService, router);
        return EMPTY;
      }
      throw err;
    }),
    switchMap(result => {
      const authedReq = req.clone({
        setHeaders: { Authorization: `Bearer ${result.accessToken}` }
      });

      return next(authedReq).pipe(
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 401) {
            if (!forceRefresh) {
              // Token was cached but rejected server-side — try a forced refresh once
              return makeAuthenticatedRequest(msalService, authService, router, req, next, activeAccount, true);
            }
            // Fresh token still rejected — user must re-authenticate
            redirectToLogin(msalService, authService, router);
            return EMPTY;
          }
          throw err;
        })
      );
    })
  );
}

function redirectToLogin(msalService: MsalService, authService: AuthService, router: Router): void {
  if (redirectInProgress) return;
  redirectInProgress = true;
  const returnUrl = router.url;
  msalService.instance.setActiveAccount(null);
  authService.clearLocalAuthState();
  router.navigate(['/auth/login'], { queryParams: { returnUrl } }).then(() => {
    redirectInProgress = false;
  });
}

/**
 * Path prefix for same-origin API calls that must carry the bearer token.
 *
 * Scarlett is reached SAME-ORIGIN in every environment — nginx proxies this prefix to her internal
 * ingress when deployed, and the Angular dev-server proxy does the same locally — so her generated
 * client issues RELATIVE URLs (`/api/v1/scarlett/...`) rather than absolute ones like Ella's.
 */
const SAME_ORIGIN_API_PREFIX = '/api/';

/**
 * Determines if the token should be attached to the request.
 *
 * Guards against token leakage to third-party domains, which is the whole point of this function —
 * any change here must preserve that. Two things are allowed, and nothing else:
 *
 *   1. The configured API origin (`environment.baseApiUrl`) — Ella, called with absolute URLs.
 *   2. A SAME-ORIGIN request under `/api/` — Scarlett, through the proxy.
 *
 * <b>Relative URLs used to fall through to `false`, silently.</b> `new URL(relative)` throws without
 * a base, so every `/api/v1/scarlett/...` call hit the `catch` and went out with no `Authorization`
 * header, and Scarlett answered 401. Nothing caught it earlier because Ella is the only other API
 * and it uses absolute URLs, so no relative request had ever needed a token.
 *
 * The `/api/` prefix matters: resolving relative URLs against the current origin would otherwise
 * make EVERY same-origin HttpClient request "attachable", and each one would then pay for an
 * `acquireTokenSilent` call. Scoping to the API path keeps assets and local JSON out.
 *
 * Same-origin is safe by construction here — a relative URL cannot address a third party.
 */
function shouldAttachToken(url: string): boolean {
  try {
    // Resolving against the app's own origin is what makes relative URLs work at all. The result is
    // then checked against both allowed origins below, so the base is a parsing aid, not a grant.
    const requestUrl = new URL(url, window.location.origin);

    if (requestUrl.origin === window.location.origin) {
      return requestUrl.pathname.startsWith(SAME_ORIGIN_API_PREFIX);
    }

    return requestUrl.origin === new URL(environment.baseApiUrl).origin;
  } catch {
    return false;
  }
}
